import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
    Alert, Box, Button, CircularProgress, Container, Divider,
    IconButton, InputAdornment, Paper, TextField, Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithEmailAndPassword,
    signOut,
} from 'firebase/auth';
import { firebaseAuth, getErrorMessage } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { post, CONNECT } from '../../services/Client';
import { UserContext } from '../App';
import logo from '../../images/pinbike-logo-animato.gif';


export const CONNECT_SESSION_SERVICE = 'pinbike_connect_service';

const isInAppBrowser = () => {
    const ua = navigator.userAgent || '';
    return /FBAN|FBAV|Instagram|Telegram|Line|MicroMessenger/i.test(ua) || /; wv\)/.test(ua);
};
export const CONNECT_SESSION_CODE = 'pinbike_connect_code';

const GOOGLE_ICON = (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
);

export default function ConnectPage() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const user = useContext(UserContext);

    const params = new URLSearchParams(location.search);
    const serviceFromUrl = params.get('service');
    const codeFromUrl = params.get('code');

    // Leggi da URL oppure da sessionStorage (sopravvive ai redirect di login/registrazione)
    const service = serviceFromUrl || sessionStorage.getItem(CONNECT_SESSION_SERVICE);
    const code = codeFromUrl || sessionStorage.getItem(CONNECT_SESSION_CODE);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Persisti in sessionStorage non appena i parametri sono disponibili
    useEffect(() => {
        if (service && code) {
            sessionStorage.setItem(CONNECT_SESSION_SERVICE, service);
            sessionStorage.setItem(CONNECT_SESSION_CODE, code);
        }
    }, [service, code]);

    // Gestione errori da signInWithRedirect (mobile)
    useEffect(() => {
        getRedirectResult(firebaseAuth).catch(e => {
            const msg = getErrorMessage(e);
            if (msg) setError(msg);
        });
    }, []);


    if (!service || !code) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Alert severity="error">{t('connectInvalidParams')}</Alert>
            </Container>
        );
    }

    // ─── Collega l'account autenticato al servizio terzo ─────────────────
    async function handleConnect() {
        setLoading(true);
        setError('');
        try {
            await post(CONNECT, { body: { service, code } });
            sessionStorage.removeItem(CONNECT_SESSION_SERVICE);
            sessionStorage.removeItem(CONNECT_SESSION_CODE);
            setSuccess(true);
        } catch (e) {
            setError(t('connectError'));
        }
        setLoading(false);
    }

    // ─── Login con Google ─────────────────────────────────────────────────
    async function handleGoogleSignIn() {
        if (isInAppBrowser()) {
            enqueueSnackbar(t('openInBrowser'), { variant: 'warning', autoHideDuration: 6000 });
            return;
        }
        setLoading(true);
        setError('');
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(firebaseAuth, provider);
            // onAuthStateChanged aggiornerà UserContext → re-render con consent screen
        } catch (e) {
            if (['auth/popup-blocked', 'auth/popup-closed-by-user', 'auth/cancelled-popup-request'].includes(e.code)) {
                await signInWithRedirect(firebaseAuth, provider);
            } else {
                const msg = getErrorMessage(e);
                if (msg) setError(msg);
            }
        }
        setLoading(false);
    }

    // ─── Logout ───────────────────────────────────────────────────────────
    async function handleLogout() {
        await signOut(firebaseAuth);
    }

    // ─── Login con email/password ─────────────────────────────────────────
    async function handleEmailSignIn(ev) {
        ev.preventDefault();
        setLoading(true);
        setError('');
        try {
            await signInWithEmailAndPassword(firebaseAuth, email, password);
            // UserContext si aggiorna via onAuthStateChanged → consent screen
        } catch (e) {
            setError(getErrorMessage(e));
        }
        setLoading(false);
    }

    // ─── Schermata di successo ────────────────────────────────────────────
    if (success) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {t('connectSuccess')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        {t('connectSuccessDescription', { service })}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('connectDownloadAppMessage')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        href="https://www.pin.bike/app-download.php"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ borderRadius: 2, px: 4 }}
                    >
                        {t('connectDownloadApp')}
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 4, mb: 6 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <img src={logo} alt="Pin Bike" style={{ height: 60 }} />
            </Box>

            <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>

                {user.isLogged ? (
                    // ─── Consent screen: utente già autenticato ───────────────
                    <>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <LinkIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                {t('connectConsentTitle', { service })}
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                                {t('connectConsentDescription', { service })}
                            </Typography>
                        </Box>

                        <Alert severity="info" sx={{ mb: 3 }}>
                            {t('connectConsentAccount', { email: user.email })}
                        </Alert>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <Button
                            fullWidth variant="contained" size="large"
                            onClick={handleConnect} disabled={loading}
                            sx={{ py: 1.5, borderRadius: 2 }}
                        >
                            {loading
                                ? <CircularProgress size={24} color="inherit" />
                                : t('connectButton', { service })}
                        </Button>

                        <Button
                            fullWidth variant="text" size="small"
                            onClick={handleLogout} sx={{ mt: 1.5 }}
                        >
                            {t('logOut')}
                        </Button>
                    </>
                ) : (
                    // ─── Form di login: utente non autenticato ────────────────
                    <>
                        <Typography variant="h6" fontWeight="bold" textAlign="center" gutterBottom>
                            {t('connectLoginTitle', { service })}
                        </Typography>
                        <Typography color="text.secondary" variant="body2" textAlign="center" sx={{ mb: 3 }}>
                            {t('connectLoginDescription', { service })}
                        </Typography>

                        <Button
                            fullWidth variant="outlined" size="large"
                            onClick={handleGoogleSignIn} disabled={loading}
                            startIcon={GOOGLE_ICON}
                            sx={{ mb: 2, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem' }}
                        >
                            {t('continueWithGoogle')}
                        </Button>

                        <Divider sx={{ my: 2 }}>
                            <Typography variant="body2" color="textSecondary">{t('or')}</Typography>
                        </Divider>

                        <Box component="form" onSubmit={handleEmailSignIn}>
                            <TextField
                                fullWidth label={t('email')} type="email"
                                value={email} onChange={e => setEmail(e.target.value)}
                                margin="normal" required autoComplete="email"
                            />
                            <TextField
                                fullWidth label={t('password')}
                                type={showPassword ? 'text' : 'password'}
                                value={password} onChange={e => setPassword(e.target.value)}
                                margin="normal" required autoComplete="current-password"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                            <Button
                                type="submit" fullWidth variant="contained" size="large"
                                disabled={loading} sx={{ mt: 2, py: 1.5, borderRadius: 2 }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : t('login')}
                            </Button>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" textAlign="center">
                            {t('noAccount')}{' '}
                            <Link
                                to={`/newPinbiker?service=${encodeURIComponent(service)}&code=${encodeURIComponent(code)}`}
                                style={{ color: 'inherit', fontWeight: 'bold' }}
                            >
                                {t('registerHere')}
                            </Link>
                        </Typography>
                    </>
                )}
            </Paper>
        </Container>
    );
}
